import type { StoreTaxSettings } from "@hobbyshopify/backend-client";
import { BaseController, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../../../app-deps";
import { loadTaxSettings, setCollectSalesTax } from "../../../../core/ops/tax-ops";
import { busyForm, failedForm, failedLoad, idleForm, loaded, loading } from "../../../../core/primitives/load-status";
import { SalesTaxSectionActions, SalesTaxSectionState } from "./sales-tax-section-model";

export type SalesTaxSectionDeps = Pick<AppDeps, "client" | "authHandler">;

export interface SalesTaxSectionHost {
  readonly storeId: string;
}

/**
 * Sales tax on card sales. Re-read when the tab comes back into view: the Stripe
 * half of the answer is written by the account sync, and a merchant who just
 * added a registration in their Stripe dashboard expects to see it here.
 */
export class SalesTaxSectionController extends BaseController implements SalesTaxSectionActions {
  readonly state = new SalesTaxSectionState();

  constructor(
    subtree: SubtreeModel,
    readonly deps: SalesTaxSectionDeps,
    readonly host: SalesTaxSectionHost,
  ) {
    super();
    subtree.put(SalesTaxSectionState, this.state);
    subtree.put(SalesTaxSectionActions, this);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.#onVisibilityChange);
      this.autoDispose(() => document.removeEventListener("visibilitychange", this.#onVisibilityChange));
    }
    void this.#load();
  }

  refresh = async () => {
    await this.#load();
  };

  toggle = async () => {
    const settings = this.state.settings.value;
    if (!settings || this.state.form.value.busy) return;
    this.state.form.value = busyForm;
    const r = await setCollectSalesTax({
      ...this.deps,
      storeId: this.host.storeId,
      collectSalesTax: !settings.collectSalesTax,
    });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.form.value = failedForm(r.errors);
      return;
    }
    this.#apply(r.value);
    this.state.form.value = idleForm;
  };

  async #load(): Promise<void> {
    if (this.state.fetching.value) return;
    this.state.fetching.value = true;
    if (this.state.status.value.kind === "idle") this.state.status.value = loading;
    const r = await loadTaxSettings({ ...this.deps, storeId: this.host.storeId });
    if (this.isDisposed) return;
    this.state.fetching.value = false;
    if (!r.ok) {
      if (this.state.status.value.kind !== "loaded") this.state.status.value = failedLoad(r.errors);
      return;
    }
    this.#apply(r.value);
    this.state.status.value = loaded;
  }

  #apply(settings: StoreTaxSettings): void {
    this.state.settings.value = settings;
    this.state.checkedAt.value = Date.now();
  }

  readonly #onVisibilityChange = (): void => {
    if (document.visibilityState === "visible") void this.#load();
  };
}
